import { DocumentSnapshot, onSnapshot, Unsubscribe, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import {
  Paths,
  Page,
  assertCurrentUid,
  debounceManager,
  debouncedSetDoc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  q,
  query,
  setDoc,
  startAfter,
  withBackoff,
} from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import { ChatThread } from '../components/GlobalChatSystem/types';
import { ChatThreadDoc, fromChatThreadDoc, toChatThreadDoc } from '../types/chatThreadIndexFirestore';
import { getActiveWorkspaceAccount, onActiveWorkspaceResolved } from '../services/activeWorkspace';
import { readConfirmedWorkspace } from '../services/prest0nRuntime/confirmedWorkspaceCache';

const THREAD_DOMAIN = 'chat_threads';
const META_DOMAIN = 'chat_threads_meta';
const ACTIVE_DOC_ID = 'active';
const WORKING_SET_LIMIT = 40;
const DEFAULT_PAGE_SIZE = 25;
const WRITE_DEBOUNCE_MS = 150;
const PREST0N_CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

const pendingWritesByUid: Map<string, Promise<void>> = new Map();

function enqueueUserWrite(uid: string, work: () => Promise<void>): Promise<void> {
  const previous = pendingWritesByUid.get(uid) || Promise.resolve();
  const next = previous.catch(() => {}).then(work);
  pendingWritesByUid.set(uid, next);
  next.finally(() => {
    if (pendingWritesByUid.get(uid) === next) {
      pendingWritesByUid.delete(uid);
    }
  }).catch(() => {});
  return next;
}

function threadRef(uid: string, threadId: string) {
  return Paths.domainDoc(uid, THREAD_DOMAIN, threadId);
}

// ── INCIDENT-20260818: deferred FULL-SHAPE creates (bind-at-birth) ──────────
// A thread create that races the workspace resolution used to go out UNBOUND
// (and a narrow two-field re-stamp could mint a PARTIAL, index-invisible doc
// when it raced the debounced create — M12). Creates now PARK the full
// payload until the workspace resolves; updates refresh the parked snapshot;
// the resolution listener flushes full create-shaped writes through the same
// per-uid queue + debounce discipline. A thread is therefore never written
// unbound and never written partial.
// `intendedAccountId` (2026-08-20): the workspace the thread was CREATED IN,
// captured at park time from the confirmed-workspace cache (the same source
// the boot guard trusts — written only on successful strict resolution). The
// flush used to bind every parked entry to whatever accountId resolved
// LATEST — a rapid workspace switch between park and flush bound org A's
// draft into org B, arming the live containment effect to yank it later.
const deferredCreates = new Map<string, {
  uid: string;
  instanceId: string;
  thread: ChatThread;
  intendedAccountId: string | null;
}>();

/**
 * Backstop-only binding stamp for docs that ALREADY EXIST unbound (legacy
 * stock the sweep has not reached). Existence-probed so it can never mint a
 * partial two-field doc (M12). Not reachable from the create path anymore.
 */
async function stampThreadWorkspaceBinding(
  uid: string,
  threadId: string,
  accountId: string,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'stampThreadWorkspaceBinding');
  assertCurrentUid(uid, 'stampThreadWorkspaceBinding');
  const ref = threadRef(uid, threadId);
  const snap = await withBackoff(() => getDoc(ref));
  if (!snap.exists()) return; // never create — the deferred-create path owns creates
  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => setDoc(ref, {
      accountId,
      sessionId: `sess_thr_${threadId}`,
    }, { merge: true }));
  });
}
export { stampThreadWorkspaceBinding };

/** Flush parked creates as FULL create-shaped writes (resolution landed). */
function flushDeferredCreates(accountId: string): void {
  if (deferredCreates.size === 0) return;
  const pending = Array.from(deferredCreates.values());
  deferredCreates.clear();
  for (const entry of pending) {
    try {
      addUserScopedWriteGuard(entry.uid, 'flushDeferredCreates');
      assertCurrentUid(entry.uid, 'flushDeferredCreates');
    } catch {
      continue; // uid changed since parking (sign-out) — sweep is the net
    }
    const ref = threadRef(entry.uid, entry.thread.id);
    const data = toChatThreadDoc(entry.thread, entry.instanceId, {
      isCreate: true,
      // Park-time intent wins over whatever workspace happened to resolve
      // latest; the resolved id remains the fallback for entries parked
      // before any confirmed workspace existed.
      accountId: entry.intendedAccountId || accountId,
    });
    void enqueueUserWrite(entry.uid, async () => {
      await withBackoff(() => debouncedSetDoc(ref, data, {
        debounceKey: ref.path,
        debounceMs: WRITE_DEBOUNCE_MS,
      }));
    }).catch((err) => {
      console.warn('[chatThreadIndexRepository] deferred-create flush failed (non-fatal)', {
        threadId: entry.thread.id,
        error: err instanceof Error ? err.message : String(err),
      });
    });
  }
}

// Module-scope registration: the chat mount publishes the resolved workspace
// (services/activeWorkspace.ts) — parked creates flush the moment it lands.
onActiveWorkspaceResolved((accountId) => flushDeferredCreates(accountId));

// Best-effort last-chance flush on page teardown: a parked create whose
// resolution never landed this page load is written UNBOUND rather than not
// at all (the corrected M4 sweep binds it within the hour; losing the doc
// entirely is the strictly worse outcome — M1's disappeared-thread class).
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (deferredCreates.size === 0) return;
    const pending = Array.from(deferredCreates.values());
    deferredCreates.clear();
    for (const entry of pending) {
      try {
        const ref = threadRef(entry.uid, entry.thread.id);
        // Park-time intent beats the old hard-null: an org-created draft
        // flushed at pagehide used to go out UNBOUND and the M4 sweep then
        // bound it to the member's canonical org — a guaranteed foreign
        // flip for multi-workspace members. No intent → unbound as before.
        const data = toChatThreadDoc(entry.thread, entry.instanceId, {
          isCreate: true,
          accountId: entry.intendedAccountId,
        });
        void setDoc(ref, data, { merge: true });
      } catch { /* best-effort */ }
    }
  });
}

function activeRef(uid: string) {
  return Paths.domainDoc(uid, META_DOMAIN, ACTIVE_DOC_ID);
}

function clearThreadSessionStorage(threadId: string): void {
  if (typeof localStorage === 'undefined') return;
  const suffix = `_${threadId}`;
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (!key.startsWith('prest0_session_')) continue;
    if (!key.endsWith(suffix)) continue;
    localStorage.removeItem(key);
  }
}

async function archiveThreadSessionOnBackend(threadId: string): Promise<void> {
  const user = getAuth().currentUser;
  if (!user) return;
  const token = await user.getIdToken(false);
  const response = await fetch(`${PREST0N_CF_BASE_URL}/prest0nSetSessionStatus`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ threadId, status: 'archived' }),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`prest0nSetSessionStatus failed: ${response.status} ${body}`);
  }
}

/**
 * P5 stage 1 (one thread universe, dual-READ): the main web surface reads BOTH
 * its canonical 'site_chat' bucket AND the legacy 'global_chat' bucket (the
 * pre-refactor default instanceId), so threads created under the old id become
 * visible again instead of being permanently stranded. Write-canonicalization
 * (re-stamping legacy docs) is stage 2, after this read soaks in prod —
 * mirroring the per-message-doc migration's staging. Internal surfaces
 * (citadel_chat, etc.) stay partitioned.
 */
const MAIN_SURFACE_INSTANCE_ID = 'site_chat';
const LEGACY_MAIN_INSTANCE_IDS = ['site_chat', 'global_chat'];

export function subscribeChatThreadIndex(
  uid: string,
  instanceId: string,
  cb: (threads: ChatThread[]) => void,
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeChatThreadIndex');
  const colRef = Paths.domainCol(uid, THREAD_DOMAIN);
  const instanceFilter = instanceId === MAIN_SURFACE_INSTANCE_ID
    ? where('instanceId', 'in', LEGACY_MAIN_INSTANCE_IDS)
    : where('instanceId', '==', instanceId);
  const threadQuery = query(
    colRef,
    instanceFilter,
    where('archived', '==', false),
    orderBy('lastActiveAt', 'desc'),
    limit(WORKING_SET_LIMIT),
  );

  // M11 (INCIDENT-20260818): this listener previously had NO error callback —
  // a missing composite index, a rules denial, or a dropped stream failed
  // SILENTLY: no server threads ever arrived and nothing was logged. Errors
  // now log loudly and resubscribe on capped backoff; the local list is
  // untouched during the outage (reconcile only runs on snapshots).
  let disposed = false;
  let inner: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelayMs = 2000;
  const attach = () => {
    if (disposed) return;
    inner = onSnapshot(threadQuery, (snapshot) => {
      retryDelayMs = 2000; // healthy snapshot resets the backoff
      const threads = snapshot.docs
        .map((docSnap) => docSnap.data() as ChatThreadDoc)
        .map(fromChatThreadDoc);
      cb(threads);
    }, (err) => {
      console.error('[chatThreadIndexRepository] thread index subscription errored — resubscribing', {
        uid,
        instanceId,
        delayMs: retryDelayMs,
        error: err instanceof Error ? err.message : String(err),
      });
      try { inner?.(); } catch { /* already torn down */ }
      inner = null;
      if (disposed) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        attach();
      }, retryDelayMs);
      retryDelayMs = Math.min(60_000, Math.round(retryDelayMs * 2));
    });
  };
  attach();
  return () => {
    disposed = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    try { inner?.(); } catch { /* ignore */ }
    inner = null;
  };
}

/**
 * Per-WORKSPACE listen window (multi-org separation round 2, 2026-08-19).
 * The main subscription above is workspace-blind — the newest
 * WORKING_SET_LIMIT threads across EVERY organization — so a busy org's
 * activity pushed a quieter org's threads out of the window entirely and its
 * rail collapsed ("threads keep changing"). This companion subscription
 * scopes the SAME query to one workspace binding, guaranteeing the active
 * org its own full window; the store merges both streams by id. Uses the
 * (instanceId, accountId, archived, lastActiveAt) composite index that
 * shipped with the M4 backfill.
 */
export function subscribeChatThreadIndexForWorkspace(
  uid: string,
  instanceId: string,
  accountId: string,
  cb: (threads: ChatThread[]) => void,
): Unsubscribe {
  assertCurrentUid(uid, 'subscribeChatThreadIndexForWorkspace');
  const colRef = Paths.domainCol(uid, THREAD_DOMAIN);
  const instanceFilter = instanceId === MAIN_SURFACE_INSTANCE_ID
    ? where('instanceId', 'in', LEGACY_MAIN_INSTANCE_IDS)
    : where('instanceId', '==', instanceId);
  const threadQuery = query(
    colRef,
    instanceFilter,
    where('accountId', '==', accountId),
    where('archived', '==', false),
    orderBy('lastActiveAt', 'desc'),
    limit(WORKING_SET_LIMIT),
  );

  // Error+backoff parity with the global listener (owner report 2026-08-19
  // "threads are disappearing"): this companion stream previously had NO
  // error callback — an index problem or rules denial silenced the ACTIVE
  // workspace's own window, so a global-only (cross-org top-40) snapshot
  // drove the reconcile and the quiet org's threads vanished. Same loud
  // log + capped-backoff resubscribe as the global stream; the merge layer
  // additionally holds reconcile drops until both streams deliver.
  let disposed = false;
  let inner: Unsubscribe | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelayMs = 2000;
  const attach = () => {
    if (disposed) return;
    inner = onSnapshot(threadQuery, (snapshot) => {
      retryDelayMs = 2000; // healthy snapshot resets the backoff
      const threads = snapshot.docs
        .map((docSnap) => docSnap.data() as ChatThreadDoc)
        .map(fromChatThreadDoc);
      cb(threads);
    }, (err) => {
      console.error('[chatThreadIndexRepository] workspace thread index subscription errored — resubscribing', {
        uid,
        instanceId,
        accountId,
        delayMs: retryDelayMs,
        error: err instanceof Error ? err.message : String(err),
      });
      try { inner?.(); } catch { /* already torn down */ }
      inner = null;
      if (disposed) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        attach();
      }, retryDelayMs);
      retryDelayMs = Math.min(60_000, Math.round(retryDelayMs * 2));
    });
  };
  attach();
  return () => {
    disposed = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    try { inner?.(); } catch { /* ignore */ }
    inner = null;
  };
}

export async function upsertChatThread(
  uid: string,
  instanceId: string,
  thread: ChatThread,
  options?: {
    /**
     * True when this thread was created locally this session and the server
     * has not yet acknowledged it. Creates stamp `archived:false` (the index
     * query needs the field to exist); updates OMIT the field so a merge can
     * never un-archive a thread archived elsewhere (BUG-THREAD-RESURRECTION).
     */
    isCreate?: boolean;
  },
): Promise<void> {
  addUserScopedWriteGuard(uid, 'upsertChatThread');
  assertCurrentUid(uid, 'upsertChatThread');
  const ref = threadRef(uid, thread.id);
  const activeAccountId = getActiveWorkspaceAccount();
  // BIND-AT-BIRTH (INCIDENT-20260818): a create racing the workspace
  // resolution is PARKED with its full payload instead of written unbound;
  // later upserts for a parked id refresh the parked snapshot (the flush
  // carries the newest state). The resolution listener flushes parked
  // entries as full create-shaped, BOUND writes; pagehide is the last-chance
  // unbound flush. A signed-in thread doc can therefore no longer be born
  // unbound while the page lives, and never partial at all.
  const isCreate = options?.isCreate === true;
  if (!activeAccountId && (isCreate || deferredCreates.has(thread.id))) {
    deferredCreates.set(thread.id, {
      uid,
      instanceId,
      thread: { ...thread },
      // Capture the intended workspace NOW (see the map's doc). A refresh of
      // an already-parked entry keeps its original intent — the create's
      // context, not the refresh's.
      intendedAccountId:
        deferredCreates.get(thread.id)?.intendedAccountId
        // Shared-thread labeling (2026-08-21): an explicit binding on the
        // thread object (a surfaced group-thread stub carrying its row's
        // workspace) is the strongest intent of all.
        ?? thread.accountId
        ?? readConfirmedWorkspace(uid)?.accountId
        ?? null,
    });
    return;
  }
  if (deferredCreates.has(thread.id)) {
    // Resolution landed and the caller upserted before the listener ran:
    // this very write completes the parked create.
    deferredCreates.delete(thread.id);
    options = { ...(options || {}), isCreate: true };
  }
  const data = toChatThreadDoc(thread, instanceId, {
    isCreate: options?.isCreate === true,
    // Multi-org P2: stamp the immutable workspace binding on creates (the
    // publish point is the chat mount — services/activeWorkspace.ts).
    // Shared-thread labeling (2026-08-21): an EXPLICIT binding on the thread
    // object (a surfaced group-thread stub carrying its membership row's
    // workspace, or a repair re-create restoring a dropped thread's true
    // home) wins over the viewer's active workspace — a thread belongs to
    // ITS org, not whichever workspace happens to be open.
    accountId: thread.accountId || activeAccountId,
  });

  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => debouncedSetDoc(ref, data, {
      debounceKey: ref.path,
      debounceMs: WRITE_DEBOUNCE_MS,
    }));
  });
}

/**
 * Cheap existence probe for a thread's index doc (2026-07-31 multi-device
 * directive). The recipient-side surfacing flow used to treat "not in the
 * local top-40 working set" as "no index doc" and re-stub the thread — a
 * merge write that clobbered the real doc's title/messageCount/lastActiveAt
 * on every reload and tab focus. A doc that exists (even outside the window)
 * must never be re-stubbed; it simply lives outside the window until real
 * activity returns it.
 */
export async function chatThreadIndexDocExists(uid: string, threadId: string): Promise<boolean> {
  assertCurrentUid(uid, 'chatThreadIndexDocExists');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  return snap.exists();
}

/**
 * Single-doc read of a thread's index doc, parsed through the shared mapper
 * (routine list parity, 2026-08-02). Powers switchThread's navigation
 * hydration: opening a routine whose thread sits OUTSIDE the capped listen
 * window needs the record on demand — the subscription can never deliver it.
 * Returns null when the doc does not exist; `archived` is carried through so
 * the caller decides how to treat archived docs (the hydrate path falls back
 * to a minimal record rather than resurrecting one).
 */
export async function getChatThread(uid: string, threadId: string): Promise<ChatThread | null> {
  assertCurrentUid(uid, 'getChatThread');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  if (!snap.exists()) return null;
  return fromChatThreadDoc(snap.data() as ChatThreadDoc);
}

/**
 * True when the thread's server doc exists and is archived. Used to confirm a
 * remote archive (thread vanished from another window's index snapshot) before
 * the local UI drops it — a thread can also leave the snapshot through
 * working-set churn, which must NOT close the user's active conversation.
 */
export async function isChatThreadArchivedOnServer(uid: string, threadId: string): Promise<boolean> {
  assertCurrentUid(uid, 'isChatThreadArchivedOnServer');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  if (!snap.exists()) return false;
  const data = snap.data() as Partial<ChatThreadDoc>;
  return data.archived === true;
}

/**
 * The archive facts on this user's own index doc — `archived` plus the
 * attribution the shared-archive fanout stamps (2026-08-26: `archivedBy`,
 * `archivedByName`), so the remote-archive announcement can SAY who archived
 * the thread instead of letting the conversation silently vanish.
 */
export interface ChatThreadArchiveInfo {
  archived: boolean;
  archivedBy?: string;
  archivedByName?: string;
}

export async function getChatThreadArchiveInfo(
  uid: string,
  threadId: string,
): Promise<ChatThreadArchiveInfo> {
  assertCurrentUid(uid, 'getChatThreadArchiveInfo');
  const snap = await withBackoff(() => getDoc(threadRef(uid, threadId)));
  if (!snap.exists()) return { archived: false };
  const data = snap.data() as Record<string, unknown>;
  return {
    archived: data.archived === true,
    ...(typeof data.archivedBy === 'string' && data.archivedBy.trim()
      ? { archivedBy: data.archivedBy.trim() } : {}),
    ...(typeof data.archivedByName === 'string' && data.archivedByName.trim()
      ? { archivedByName: data.archivedByName.trim() } : {}),
  };
}

export async function setActiveThread(
  uid: string,
  instanceId: string,
  threadId: string,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'setActiveThread');
  assertCurrentUid(uid, 'setActiveThread');
  const ref = activeRef(uid);

  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => debouncedSetDoc(ref, {
      [instanceId]: threadId,
    }, {
      debounceKey: ref.path,
      debounceMs: WRITE_DEBOUNCE_MS,
    }));
  });
}

export async function getActiveThread(uid: string, instanceId: string): Promise<string | null> {
  assertCurrentUid(uid, 'getActiveThread');
  const snap = await withBackoff(() => getDoc(activeRef(uid)));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  const value = data[instanceId];
  return typeof value === 'string' ? value : null;
}

export async function archiveChatThread(
  uid: string,
  instanceId: string,
  threadId: string,
): Promise<void> {
  addUserScopedWriteGuard(uid, 'archiveChatThread');
  assertCurrentUid(uid, 'archiveChatThread');
  // A create still PARKED (workspace unresolved) that gets archived must not
  // flush later as a fresh `archived:false` doc — the resolution listener and
  // the pagehide flush both write create-shaped payloads and would resurrect
  // the thread. Dropping the parked entry makes this archive the last word.
  deferredCreates.delete(threadId);
  const ref = threadRef(uid, threadId);

  await enqueueUserWrite(uid, async () => {
    // BUG-THREAD-RESURRECTION: the archive must NOT ride the shared
    // debounce. Upserts and archives both key on ref.path, and
    // DebounceManager.schedule cancels the pending task for its key — so
    // a debounced archive could be silently discarded by a racing upsert,
    // and a queued upsert flushing after the archive would re-stamp
    // archived:false. Cancel whatever is pending for this doc, then write
    // archived:true immediately. This runs inside the per-uid queue, so
    // an upsert enqueued ahead of the archive has already scheduled its
    // debounce by the time we run — and gets cancelled here.
    debounceManager.cancel(ref.path);
    await withBackoff(() => setDoc(ref, { archived: true, instanceId }, { merge: true }));
  });

  clearThreadSessionStorage(threadId);
  await archiveThreadSessionOnBackend(threadId).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn('[chatThreadIndexRepository] Failed to archive backend thread session', { threadId, error });
  });
}

/**
 * ROUTINE HANDOFF ACK (Routine Threads, 2026-07-31): stamp
 * `routineHandoffAckAt` (ISO now) onto a routine thread's index doc after the
 * client auto-navigated to it — the one routine-mirror field the CLIENT owns.
 * The ack is the handoff effect's cross-device idempotence guard (a second
 * device's snapshot sees the ack and never re-navigates), so like
 * archiveChatThread it must NOT ride the shared debounce: a merge write, sent
 * immediately through the per-uid queue.
 */
export async function acknowledgeRoutineHandoff(uid: string, threadId: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'acknowledgeRoutineHandoff');
  assertCurrentUid(uid, 'acknowledgeRoutineHandoff');
  const ref = threadRef(uid, threadId);

  await enqueueUserWrite(uid, async () => {
    await withBackoff(() => setDoc(ref, { routineHandoffAckAt: new Date().toISOString() }, { merge: true }));
  });
}

export async function loadOlderThreads(
  uid: string,
  instanceId: string,
  cursor: DocumentSnapshot | null,
  pageSize = DEFAULT_PAGE_SIZE,
): Promise<Page<ChatThread>> {
  assertCurrentUid(uid, 'loadOlderThreads');

  const colRef = Paths.domainCol(uid, THREAD_DOMAIN);
  const scopedRef = q.whereEq(colRef, 'instanceId', instanceId);
  const pagingBase = q.paginated(scopedRef as any, 'lastActiveAt', { limit: pageSize });
  const pagingQuery = cursor ? query(pagingBase, startAfter(cursor)) : pagingBase;

  const snap = await withBackoff(() => getDocs(pagingQuery));
  const items = snap.docs.map((docSnap) => fromChatThreadDoc(docSnap.data() as ChatThreadDoc));
  const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
  return { items, nextCursor };
}

export function __resetPendingWritesByUidForTests(): void {
  pendingWritesByUid.clear();
}
